import { memo, useMemo } from 'react';
import { Textarea } from '@heroui/react';
import RadioGroupField from '@/components/RadioGroupField';
import { Reactivo, ReactivoOpcion } from '@/hooks/useEvaluacion';
import { observacionVacia, opcionRequiereObservacion } from './helpers';

interface ReactivoRowProps {
    reactivo: Reactivo;
    opciones: ReactivoOpcion[];
    /**
     * Map precalculado nOpcion -> opcion. Si el padre lo pasa, se usa para
     * el lookup O(1) de la opción seleccionada. Si no, se hace fallback a
     * `opciones.find` (compatibilidad con tests o consumidores que aún no
     * enriquecen el shape).
     */
    opcionesByOpcion?: Map<number, ReactivoOpcion>;
    seleccion?: number;
    observacion?: string;
    onSeleccion: (nOpcion: number) => void;
    onObservacion: (texto: string) => void;
    isDisabled?: boolean;
}

// Fila de reactivo nTipo 1: el texto del reactivo a la izquierda, el RadioGroup
// con las opciones (y su ponderación en % junto a cada una) al centro, y el
// textarea de observaciones a la derecha cuando bMostrarObservaciones lo pide.
// En pantallas pequeñas los tres bloques se apilan.
// nTipo 2: reactivo abierto — texto arriba y textarea libre a todo el ancho.
// La contestación vive en el mismo campo `observacion` (cObservacion) para
// reutilizar el shape de respuestas/Payload sin tocar el backend.
//
// Componente memoizado: las props de callback (`onSeleccion`, `onObservacion`)
// se reusan a lo largo de la vida del row (el padre las crea inline) y NO
// afectan el output, así que se ignoran en el comparator. Esto evita que
// cada keystroke en un textarea re-renderice los R reactivos de la página.
const ReactivoRow = ({
    reactivo,
    opciones,
    opcionesByOpcion,
    seleccion,
    observacion,
    onSeleccion,
    onObservacion,
    isDisabled,
}: ReactivoRowProps) => {
    // nTipo 1: el array `answers` para RadioGroupField se memoiza por `opciones`
    // para que la referencia sea estable entre renders y `cn(...)` interno de
    // RadioGroupField no genere strings nuevos por keystroke. Se declara
    // ANTES de los returns tempranos para cumplir la regla de los hooks.
    const answers = useMemo(
        () => opciones.map((opcion) => ({
            nRespuesta: opcion.nOpcion,
            cDescripcion: opcion.cOpcion,
            ponderacion: opcion.nValorPonderacion,
        })),
        [opciones],
    );

    if (reactivo.nTipo !== 1 && reactivo.nTipo !== 2) {
        return (
            <div className="border-b border-default-200 py-4">
                <strong className="text-base leading-snug">{reactivo.cReactivo}</strong>
                <p className="text-sm text-default-500">
                    Tipo de reactivo no soportado (nTipo: {reactivo.nTipo}).
                </p>
            </div>
        );
    }

    // nTipo 2: reactivo de respuesta libre. Sin RadioGroup ni opciones; el
    // textarea ocupa el ancho completo bajo el enunciado. El valor se persiste
    // en el mismo `cObservacion` que nTipo 1, así mapearRespuestasGuardadas y
    // construirPayloadGuardado no necesitan cambios. La obligatoriedad se
    // rige por bOpcional: 0 = obligatorio (bloquea Enviar si está vacío),
    // cualquier valor truthy = opcional.
    if (reactivo.nTipo === 2) {
        const observacionObligatoria = !Number(reactivo.bOpcional);
        const observacionFaltante = observacionObligatoria && observacionVacia(observacion);

        return (
            <div className="border-b border-default-200 py-4">
                <strong className="text-base leading-snug block pb-2">
                    {reactivo.cReactivo}
                </strong>
                <Textarea
                    aria-label={`Respuesta: ${reactivo.cReactivo}`}
                    placeholder={observacionObligatoria ? 'Respuesta (obligatorio)...' : 'Respuesta...'}
                    value={observacion ?? ''}
                    onValueChange={onObservacion}
                    isDisabled={isDisabled}
                    isRequired={observacionObligatoria}
                    errorMessage={
                        observacionFaltante
                            ? 'La respuesta es obligatoria'
                            : undefined
                    }
                    minRows={3}
                    maxRows={8}
                    classNames={{
                        input: 'bg-default-200 rounded',
                        inputWrapper: 'bg-default-200',
                    }}
                />
            </div>
        );
    }

    // Derivaciones de obligatoriedad del textarea, hoisted para no anidar un
    // IIFE dentro del JSX. Lookup O(1) cuando el padre pasa `opcionesByOpcion`;
    // fallback a `find` por compatibilidad.
    const opcionSeleccionada = seleccion != null
        ? (opcionesByOpcion?.get(seleccion) ?? opciones.find((opcion) => opcion.nOpcion === seleccion))
        : undefined;
    const observacionObligatoria = opcionRequiereObservacion(opcionSeleccionada);
    const observacionFaltante = observacionObligatoria && observacionVacia(observacion);

    return (
        <div
            className={`
                grid grid-cols-[1.4fr_auto_minmax(220px,1.1fr)] items-start
                gap-x-6 gap-y-2 border-b border-default-200 py-4
                max-lg:grid-cols-1
            `}
        >
            <strong className="text-base leading-snug">{reactivo.cReactivo}</strong>

            <RadioGroupField
                value={seleccion != null ? String(seleccion) : ''}
                answers={answers}
                setValue={(value) => onSeleccion(Number(value))}
                isDisabled={isDisabled}
            />

            {reactivo.bMostrarObservaciones ? (
                <Textarea
                    aria-label={`Observaciones: ${reactivo.cReactivo}`}
                    placeholder={observacionObligatoria ? 'Observaciones (obligatorio)...' : 'Observaciones...'}
                    value={observacion ?? ''}
                    onValueChange={onObservacion}
                    isDisabled={isDisabled}
                    isRequired={observacionObligatoria}
                    errorMessage={
                        observacionFaltante
                            ? 'La observación es obligatoria.'
                            : undefined
                    }
                    minRows={2}
                    maxRows={4}
                    classNames={{
                        input: 'bg-default-200 rounded',
                        inputWrapper: 'bg-default-200',
                    }}
                />
            ) : null}
        </div>
    );
};

// Comparator custom: ignora los callbacks (siempre nuevos por render, no
// afectan el output) y compara el resto por referencia/valor. Como el padre
// (index.tsx) ahora memoiza `secciones` y pasa el mismo array de opciones
// por reactivo, las referencias a `opciones`/`opcionesByOpcion`/`reactivo`
// se mantienen estables mientras la evaluación no cambie.
export default memo(ReactivoRow, (prev, next) =>
    prev.reactivo === next.reactivo
    && prev.opciones === next.opciones
    && prev.opcionesByOpcion === next.opcionesByOpcion
    && prev.seleccion === next.seleccion
    && prev.observacion === next.observacion
    && prev.isDisabled === next.isDisabled,
);
